import { zodResolver } from "@hookform/resolvers/zod";
import { DateTime } from "luxon";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useFolder, useFolderCustomerFolderId } from "src/core/contexts/FolderContext";
import { useRiskOptions } from "src/core/contexts/RiskOptionsContext";
import {
  UpsertMissionFormValues,
  upsertMissionSchema,
} from "src/core/forms/missions/upsertMission.schema";
import {
  DataType,
  MissionStatus,
  useCreateMissionMutation,
  useUpdateMissionMutation,
} from "src/core/gql/graphql";
import { IMission } from "src/core/hooks/folder/useFolderMissions";
import { useToast } from "src/core/hooks/use-toast";

const getDefaultRenewDate = (): string => {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return DateTime.fromJSDate(date).setZone("local").startOf("day").toISO() || "";
};

export const useUpsertMissionForm = (
  setOpen: (open: boolean) => void,
  mission: IMission | null,
  setMissionToEdit: (mission: IMission | null) => void
) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { refetch } = useFolder();
  const { missionTypeWithRiskOptions } = useRiskOptions();
  const customerFolderId = useFolderCustomerFolderId();
  const { toast } = useToast();
  const [createMission, { loading: createMissionLoading }] = useCreateMissionMutation();
  const [updateMission, { loading: updateMissionLoading }] = useUpdateMissionMutation();
  const form = useForm<UpsertMissionFormValues>({
    resolver: (values, context, options) =>
      zodResolver(upsertMissionSchema(t, mission !== null))(values, context, options),
    mode: "onSubmit",
    defaultValues: {
      missionType: "",
      missionTypes: [],
      renewDate: getDefaultRenewDate(),
    },
  });

  // Réinitialiser le formulaire quand la mission change
  useEffect(() => {
    if (mission) {
      form.reset({
        missionType: mission.missionTypeCode,
        missionTypes: [],
        renewDate: mission.renewDate ?? "",
      });
    } else {
      form.reset({
        missionType: "",
        missionTypes: [],
        renewDate: getDefaultRenewDate(),
      });
    }
  }, [mission, form]);

  const onSubmit = useCallback(
    async (data: UpsertMissionFormValues) => {
      try {
        setIsLoading(true);

        if (!customerFolderId) {
          throw new Error("Aucun id de société trouvé");
        }

        if (mission !== null) {
          await updateMission({
            variables: {
              missionId: mission?.id,
              inputValues: [
                {
                  fieldCode: "missionTypeCode",
                  value: data.missionType!,
                  dataType: DataType.Code,
                },
                { fieldCode: "renewDate", value: data.renewDate, dataType: DataType.Timestamp },
              ],
              shouldReopen: mission.status === MissionStatus.Closed,
            },
          });
        } else {
          await Promise.all(
            (data.missionTypes ?? []).map((missionTypeCode) =>
              createMission({
                variables: {
                  customerFolderId,
                  missionTypeCode,
                  renewDate: data.renewDate,
                },
              })
            )
          );
        }

        await refetch();

        toast({
          description: t(mission ? "form.editMission.success" : "form.addMission.success"),
        });

        // Réinitialiser le formulaire
        form.reset({
          missionType: "",
          missionTypes: [],
          renewDate: getDefaultRenewDate(),
        });
      } catch (error) {
        console.error("useAddMissionForm::error", error);
        toast({
          description: t(mission ? "form.editMission.fail" : "form.addMission.fail"),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
        setMissionToEdit(null);
        setOpen(false);
      }
    },
    [
      toast,
      t,
      customerFolderId,
      setOpen,
      form,
      createMission,
      refetch,
      mission,
      updateMission,
      setMissionToEdit,
    ]
  );

  return {
    form,
    onSubmit,
    isLoading: isLoading || createMissionLoading || updateMissionLoading,
    missionTypeWithRiskOptions,
  };
};
